import React, { useState } from "react";
import { scaleLinear, scaleSqrt } from "d3-scale";
import { Figure, Tooltip, XAxis, formatNumber, useWidth } from "./shared";

const ROW = 30;
const MIN_WIDTH = 620;

// Meta-analysis forest plot: one row per study, pooled estimate as a diamond.
// Props (from Rails):
//   number, title, note, effectLabel, xDomain, xTicks, favors: [left, right],
//   studies: [{ label, n, effect, lo, hi, weight }], pooled: { label, effect, lo, hi }
export default function ForestPlot({ number, title, note, effectLabel, xDomain, xTicks, favors, studies, pooled }) {
	const [ref, measured] = useWidth();
	const [active, setActive] = useState(null);
	const width = Math.max(MIN_WIDTH, measured);

	const cols = { study: 0, n: 190, plotLeft: 250, plotRight: width - 150, estimate: width - 140 };
	const top = 34;
	const rows = studies.length + 1;
	const plotBottom = top + rows * ROW + 6;
	const height = plotBottom + 64;

	const x = scaleLinear().domain(xDomain).range([cols.plotLeft, cols.plotRight]);
	const size = scaleSqrt().domain([0, Math.max(...studies.map((s) => s.weight))]).range([3, 9]);
	const rowY = (i) => top + i * ROW + ROW / 2;
	const pooledY = rowY(studies.length) + 6;
	const ci = (r) => `${formatNumber(r.effect, 2)} [${formatNumber(r.lo, 2)}, ${formatNumber(r.hi, 2)}]`;

	const table = {
		columns: ["Study", "n", effectLabel, "95% CI", "Weight (%)"],
		rows: [
			...studies.map((s) => [s.label, s.n.toLocaleString(), formatNumber(s.effect, 2), `[${formatNumber(s.lo, 2)}, ${formatNumber(s.hi, 2)}]`, formatNumber(s.weight, 1)]),
			[pooled.label, studies.reduce((sum, s) => sum + s.n, 0).toLocaleString(), formatNumber(pooled.effect, 2), `[${formatNumber(pooled.lo, 2)}, ${formatNumber(pooled.hi, 2)}]`, "100.0"],
		],
	};

	const hovered = active === null ? null : active === "pooled" ? pooled : studies[active];

	return (
		<Figure number={number} title={title} note={note} table={table}>
			<div ref={ref} className="overflow-x-auto">
				<div className="relative" style={{ width }}>
					<svg width={width} height={height} className="block font-sans" role="group" aria-label={`Figure ${number}: ${title}`}>
						{/* Column headers */}
						<g fontSize="12.5" fontWeight="600" fill="var(--color-ink)">
							<text x={cols.study} y={16}>Study</text>
							<text x={cols.n + 40} y={16} textAnchor="end">n</text>
							<text x={cols.estimate} y={16}>{effectLabel.split(" (")[0]} [95% CI]</text>
						</g>
						<line x1="0" x2={width} y1={top - 8} y2={top - 8} stroke="var(--color-ink)" strokeWidth="1" shapeRendering="crispEdges" />

						{/* Line of no effect */}
						<line x1={x(0)} x2={x(0)} y1={top - 4} y2={plotBottom} stroke="var(--color-gray-400)" strokeWidth="1" shapeRendering="crispEdges" />

						{studies.map((s, i) => {
							const cy = rowY(i);
							const isActive = active === i;
							return (
								<g
									key={s.label}
									tabIndex={0}
									role="img"
									aria-label={`${s.label}, n = ${s.n}, ${ci(s)}`}
									onPointerEnter={() => setActive(i)}
									onPointerLeave={() => setActive(null)}
									onFocus={() => setActive(i)}
									onBlur={() => setActive(null)}
									className="focus:outline-none"
								>
									<rect x="0" y={cy - ROW / 2} width={width} height={ROW} fill={isActive ? "var(--color-gray-50)" : "transparent"} />
									<text x={cols.study} y={cy} dy="0.32em" fontSize="13" fill="var(--color-ink)">{s.label}</text>
									<text x={cols.n + 40} y={cy} dy="0.32em" fontSize="13" textAnchor="end" fill="var(--color-gray-700)" className="dp-figures">{s.n.toLocaleString()}</text>
									<line x1={x(s.lo)} x2={x(s.hi)} y1={cy} y2={cy} stroke="var(--color-gray-700)" strokeWidth="2" strokeLinecap="round" />
									<rect
										x={x(s.effect) - size(s.weight)} y={cy - size(s.weight)}
										width={size(s.weight) * 2} height={size(s.weight) * 2}
										fill="var(--color-gray-700)" stroke="var(--color-paper)" strokeWidth="2"
									/>
									<text x={cols.estimate} y={cy} dy="0.32em" fontSize="13" fill="var(--color-gray-700)" className="dp-figures">{ci(s)}</text>
								</g>
							);
						})}

						{/* Pooled estimate */}
						<g
							tabIndex={0}
							role="img"
							aria-label={`${pooled.label}, ${ci(pooled)}`}
							onPointerEnter={() => setActive("pooled")}
							onPointerLeave={() => setActive(null)}
							onFocus={() => setActive("pooled")}
							onBlur={() => setActive(null)}
							className="focus:outline-none"
						>
							<line x1="0" x2={width} y1={pooledY - ROW / 2 - 2} y2={pooledY - ROW / 2 - 2} stroke="var(--color-gray-300)" strokeWidth="1" shapeRendering="crispEdges" />
							<text x={cols.study} y={pooledY} dy="0.32em" fontSize="13" fontWeight="600" fill="var(--color-ink)">{pooled.label}</text>
							<path
								d={`M${x(pooled.lo)},${pooledY} L${x(pooled.effect)},${pooledY - 8} L${x(pooled.hi)},${pooledY} L${x(pooled.effect)},${pooledY + 8} Z`}
								fill="var(--color-series-1)"
							/>
							<text x={cols.estimate} y={pooledY} dy="0.32em" fontSize="13" fontWeight="600" fill="var(--color-ink)" className="dp-figures">{ci(pooled)}</text>
						</g>

						<XAxis
							scale={x} ticks={xTicks} y={plotBottom}
							format={(t) => formatNumber(t, 1)}
							width={cols.plotRight - cols.plotLeft} left={cols.plotLeft}
						/>
						<g fontSize="12" fill="var(--color-gray-600)">
							<text x={x(0) - 8} y={plotBottom + 40} textAnchor="end">{`← ${favors[0]}`}</text>
							<text x={x(0) + 8} y={plotBottom + 40}>{`${favors[1]} →`}</text>
						</g>
					</svg>

					{hovered && (
						<Tooltip
							x={x(hovered.hi)}
							y={active === "pooled" ? pooledY - 20 : rowY(active) - 20}
							containerWidth={width}
						>
							<div className="dp-figures font-semibold text-ink">{ci(hovered)}</div>
							<div className="text-gray-600">{hovered.label}</div>
							{hovered.weight && (
								<div className="dp-figures text-gray-600">{`n = ${hovered.n.toLocaleString()}, weight ${formatNumber(hovered.weight, 1)}%`}</div>
							)}
						</Tooltip>
					)}
				</div>
			</div>
		</Figure>
	);
}
